import { Color, Icon, Input, InputProps, InputRef, Spacing } from '@billy/design-system'
import { css } from '@emotion/core'
import styled from '@emotion/styled'
import debounce from 'lodash/debounce'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClickAway } from 'react-use'

import { reactClass } from '../../utils'
import * as Styled from './styles'

interface IconWrapperProps {
  disabled?: boolean
}

const IconWrapper = styled.figure<IconWrapperProps>`
  display: flex;
  height: 100%;
  position: absolute;
  top: 0;
  right: ${Spacing.S12};
  align-items: center;
  z-index: 1;
  cursor: pointer;
`

export type SearchBoxIconProps = InputProps & {
  clearable?: boolean
  disabled?: boolean
  onValueChange: (value: string) => void
  placeholder?: string
  defaultValue?: string
  isActive?: boolean
  isFocused?: boolean
  activeBackgroundOverride?: string
  backgroundOverride?: string
  iconColorOverride?: string
  inActiveIconColorOverride?: string
  color?: string
}

export const SearchBoxIcon = ({
  clearable = true,
  disabled,
  onValueChange,
  placeholder,
  defaultValue,
  activeBackgroundOverride,
  backgroundOverride,
  iconColorOverride,
  inActiveIconColorOverride,
  isActive = false,
  isFocused = false,
  color = 'inherit',
  ...rest
}: SearchBoxIconProps) => {
  const { t } = useTranslation()
  const inputRef = useRef<InputRef>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState('')
  const [isActivated, setIsActivated] = useState<boolean>(false)
  const debouncedOnChange = useMemo(() => debounce(onValueChange, 250), [onValueChange])

  useClickAway(containerRef, () => setIsActivated(false))

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnChange(e.target.value)
      setInputValue(e.target.value)
    },
    [debouncedOnChange],
  )

  const handleClear = () => {
    if (inputRef.current) {
      onValueChange('')
      setInputValue('')
    }
  }

  const handleToggle = () => {
    if (!isActivated) {
      setIsActivated(true)
      inputRef.current && inputRef.current.focus()
    }
  }

  useEffect(() => {
    if (isFocused) {
      inputRef.current && inputRef.current.focus()
    }
  }, [isFocused])

  const activeOrActivatedInputClass = reactClass('active-or-activated-input')
  return (
    <Styled.SearchBoxWithIconWrapper ref={containerRef}>
      <IconWrapper>{!inputValue && <Icon icon="search" onClick={handleToggle} />}</IconWrapper>

      <Styled.DynamicInputWrapper>
        <Input
          className={isActivated || isActive ? activeOrActivatedInputClass : ''}
          allowClear={true}
          onClear={handleClear}
          ref={inputRef}
          disabled={disabled}
          placeholder={placeholder || `${t('search')}...`}
          onChange={handleOnChange}
          defaultValue={defaultValue || ''}
          css={css`
            padding-right: 30px;
            background: ${isActive ? Color.White : 'transparent'};
            transition: all 200ms ease;

            &.${activeOrActivatedInputClass} {
              pointer-events: inherit;
              transform: translateX(0px);
              opacity: 1;
              width: 120px;
            }
            &:not(.${activeOrActivatedInputClass}) {
              pointer-events: none;
              transform: translateX(80px);
              opacity: 0;
              width: 20px;
            }
          `}
        />
      </Styled.DynamicInputWrapper>
    </Styled.SearchBoxWithIconWrapper>
  )
}
